import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SwaggerUI from "swagger-ui";
import "swagger-ui/dist/swagger-ui.css";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import API from "../../helper/AxiosInterceptor";

import './Apis.css';

const Apis = () => {
  const [activeSection, setActiveSection] = useState("section-intro");
  const [tokens, setTokens] = useState([]);
  const [fiatCurrencies, setFiatCurrencies] = useState([]);

  const fetchTokens = async () => {
    try {
      const response = await API.get(
        `${process.env.REACT_APP_API_URL}/token/list`,
        { params: { limitVal: 100 } }
      );
      if (response?.data?.data) {
        setTokens(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching tokens:", error);
    }
  };

  useEffect(() => {
    const isTestnetEnv = process.env.REACT_APP_NETWORK_MODE === 'testnet';
    SwaggerUI({
      dom_id: "#swagger-ui",
      url: isTestnetEnv ? "testnet.yaml" : "new.yaml",
    });
    fetchTokens();
    fetchFiatCurrencies();

    const handleScroll = () => {
      const sections = document.querySelectorAll(".api-doc-section");
      const scrollPosition = window.scrollY + 250;
      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.clientHeight;
        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveSection(section.id);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    // Handle initial hash routing
    if (window.location.hash) {
      setTimeout(() => {
        const id = window.location.hash.replace("#", "");
        const element = document.getElementById(id);
        if (element) {
          const y = element.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: y, behavior: "smooth" });
          setActiveSection(id);
        }
      }, 500);
    }

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
      setActiveSection(sectionId);
      window.history.pushState(null, "", `#${sectionId}`);
    }
  };

  const fetchFiatCurrencies = async () => {
    try {
      const response = await API.get(
        `${process.env.REACT_APP_API_URL}/fiat-currency/list`
      );
      if (response?.data?.data) {
        setFiatCurrencies(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching fiat currencies:", error);
    }
  };

  /* ──── reusable style objects ──── */
  const styles = {
    baseUrl: { background: '#f8f9fa', padding: '14px 18px', borderRadius: '8px', marginBottom: '20px', borderLeft: '4px solid #FFCE31', fontSize: '0.95rem' },
    endpointPost: { background: '#e8f5e9', padding: '10px 14px', borderRadius: '6px', marginBottom: '15px', fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: 600 },
    endpointGet: { background: '#e3f2fd', padding: '10px 14px', borderRadius: '6px', marginBottom: '15px', fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: 600 },
    codeBlock: { background: '#1e1e1e', color: '#d4d4d4', padding: '16px', borderRadius: '8px', overflow: 'auto', fontSize: '13px', marginBottom: '16px', lineHeight: 1.5 },
    responseBlock: { background: '#1e1e1e', color: '#d4d4d4', padding: '16px', borderRadius: '8px', overflow: 'auto', fontSize: '13px', marginBottom: '16px', lineHeight: 1.5 },
    tipBox: { background: '#fff3cd', padding: '12px 16px', borderRadius: '6px', borderLeft: '4px solid #ffc107', marginTop: '15px', marginBottom: '20px', fontSize: '0.9rem' },
    infoBox: { background: '#e7f3ff', padding: '12px 16px', borderRadius: '6px', borderLeft: '4px solid #2196f3', marginTop: '15px', marginBottom: '20px', fontSize: '0.9rem' },
    successBox: { background: '#e8f5e9', padding: '12px 16px', borderRadius: '6px', borderLeft: '4px solid #4CAF50', marginTop: '15px', marginBottom: '20px', fontSize: '0.9rem' },
    hr: { margin: '35px 0', borderColor: '#eee' },
    subHeading: { marginTop: '20px', color: '#333', fontWeight: 600 },
  };

  const ParamTable = ({ rows }) => (
    <table className="table api-param-table">
      <thead><tr><th>Parameter</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}><td><code>{r[0]}</code></td><td>{r[1]}</td><td>{r[2]}</td><td>{r[3]}</td></tr>
        ))}
      </tbody>
    </table>
  );

  const SimpleTable = ({ headers, rows }) => (
    <table className="table api-param-table">
      <thead><tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );

  /* ──── sidebar items ──── */
  const sidebarItems = [
    { id: "section-intro", label: "Introduction" },
    { id: "section-auth", label: "Authentication" },
    { id: "section-tokens", label: "Supported Tokens" },
    { id: "section-deposit", label: "Deposit API" },
    { id: "section-withdrawal", label: "Withdrawal API" },
    { id: "section-convert", label: "Currency Conversion" },
    { id: "section-webhook", label: "Webhook" },
    { id: "section-playground", label: "Playground" },
  ];

  return (
    <>
      <Header />
      <div className="site-bg">
        <section className="inner-page-head">
          <div className="container">
            <h1>API Documentation</h1>
            <ul className="breadcrumb">
              <li className="home"><Link to="/">Home</Link></li>
              <li><Link to="/apis">&rarr; APIs</Link></li>
            </ul>
          </div>
        </section>

        <section className="api-section py-5">
          <div className="container">
            <div className="row">

              {/* ───── SIDEBAR ───── */}
              <nav className="col-sm-3" id="sidebar">
                <div id="list-example" className="list-group">
                  {sidebarItems.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className={`list-group-item list-group-item-action ${activeSection === item.id ? "active" : ""}`}
                      onClick={(e) => handleNavClick(e, item.id)}
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </nav>

              {/* ───── MAIN CONTENT ───── */}
              <div className="col-sm-9">
                <div className="d-flex flex-column gap-4">

                  {/* ════════════════ INTRODUCTION ════════════════ */}
                  <div id="section-intro" className="api-doc-section">
                    <h1>PayCoinz Payment Gateway</h1>
                    <p><strong>PayCoinz</strong> is a modern payment gateway that enables merchants to accept both <strong>crypto</strong> and <strong>fiat</strong> payments through a single, secure payment link. It allows businesses to seamlessly collect payments from customers using digital assets as well as traditional currencies.</p>

                    <h4>🌟 Key Features</h4>
                    <SimpleTable
                      headers={["Feature", "Description"]}
                      rows={[
                        [<strong>Multi-Currency Support</strong>, "Accept BTC, ETH, BNB, TRX, USDT, USDC across multiple blockchains"],
                        [<strong>Fiat Payments</strong>, `Support for ${fiatCurrencies.length > 0 ? fiatCurrencies.map(fc => fc.code.toUpperCase()).join(", ") : "loading..."} fiat currencies`],
                        [<strong>User Withdrawals</strong>, "Allow your users to withdraw crypto directly from your platform"],
                        [<strong>Auto-Approval</strong>, "Configure auto-approval limits for small withdrawals"],
                        [<strong>Webhooks</strong>, "Real-time notifications for payment and withdrawal events"],
                        [<strong>Metadata Tracking</strong>, "Attach custom data to payments for order management"],
                        [<strong>Currency Conversion</strong>, "Real-time crypto ↔ fiat conversion rates"],
                      ]}
                    />

                    {/* ── Dual Environment Card ── */}
                    <div style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      marginTop: '20px',
                      marginBottom: '20px',
                      background: '#fff',
                    }}>
                      <div style={{
                        background: '#f8f9fa',
                        padding: '12px 20px',
                        borderBottom: '1px solid #e0e0e0',
                        fontWeight: 700,
                        fontSize: '15px',
                        color: '#333',
                      }}>
                        🌐 API Environments
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {/* Production */}
                        <div style={{
                          flex: '1 1 280px',
                          padding: '20px',
                          borderRight: '1px solid #e0e0e0',
                        }}>
                          <div style={{
                            display: 'inline-block',
                            background: '#e8f5e9',
                            color: '#2e7d32',
                            padding: '3px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 700,
                            marginBottom: '10px',
                          }}>🟢 PRODUCTION (Mainnet)</div>
                          <div style={{ marginBottom: '6px' }}>
                            <strong>Base URL:</strong>{' '}
                            <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' }}>
                              https://api.paycoinz.com
                            </code>
                          </div>
                          <div style={{ marginBottom: '6px' }}>
                            <strong>Dashboard:</strong>{' '}
                            <a href="https://paycoinz.com" target="_blank" rel="noreferrer" style={{ color: '#1976d2' }}>
                              paycoinz.com
                            </a>
                          </div>
                          <div style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>
                            Real transactions with real crypto assets.
                          </div>
                        </div>

                        {/* Testnet */}
                        <div style={{
                          flex: '1 1 280px',
                          padding: '20px',
                          background: '#fffbeb',
                        }}>
                          <div style={{
                            display: 'inline-block',
                            background: '#fff3cd',
                            color: '#e65100',
                            padding: '3px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 700,
                            marginBottom: '10px',
                          }}>🟡 SANDBOX (Testnet)</div>
                          <div style={{ marginBottom: '6px' }}>
                            <strong>Base URL:</strong>{' '}
                            <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' }}>
                              https://testapi.paycoinz.com
                            </code>
                          </div>
                          <div style={{ marginBottom: '6px' }}>
                            <strong>Dashboard:</strong>{' '}
                            <a href="https://test.paycoinz.com" target="_blank" rel="noreferrer" style={{ color: '#1976d2' }}>
                              test.paycoinz.com
                            </a>
                          </div>
                          <div style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>
                            <strong>Networks:</strong> Sepolia, BSC Testnet, Polygon Amoy, Tron Shasta, Bitcoin Testnet
                          </div>
                        </div>
                      </div>

                      {/* Info footer */}
                      <div style={{
                        background: '#e7f3ff',
                        padding: '10px 20px',
                        borderTop: '1px solid #e0e0e0',
                        fontSize: '13px',
                        color: '#1565c0',
                      }}>
                        💡 <strong>Tip:</strong> Use the Testnet to develop and test your integration before going live. Same login credentials work on both environments. No real funds are ever used on testnet.
                      </div>
                    </div>

                    <div style={styles.baseUrl}>
                      <strong>🔗 Current Base URL:</strong> <code>{process.env.REACT_APP_API_URL}</code>
                      {process.env.REACT_APP_NETWORK_MODE === 'testnet' && (
                        <span style={{
                          marginLeft: '10px',
                          background: '#fff3cd',
                          color: '#e65100',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '12px',
                          fontWeight: 700,
                        }}>TESTNET</span>
                      )}
                    </div>

                    <h4>⚡ Quick Start</h4>
                    <ol>
                      <li><strong>Create an app</strong> in your PayCoinz dashboard</li>
                      <li><strong>Copy your API credentials</strong> (appId, apiKey, secretKey)</li>
                      <li><strong>Set up your webhook URL</strong> to receive notifications</li>
                      <li><strong>Configure withdrawal settings</strong> for user payouts</li>
                      <li><strong>Start integrating</strong> using the API endpoints below</li>
                    </ol>

                    <h4>📡 All API Endpoints</h4>
                    <SimpleTable
                      headers={["Method", "Endpoint", "Description"]}
                      rows={[
                        [<strong>POST</strong>, <code>/payment-link/add</code>, "Create a payment link (crypto or fiat)"],
                        [<strong>GET</strong>, <code>/payment-link/get-tx-status</code>, "Check payment status"],
                        [<strong>POST</strong>, <code>/user-withdrawal/request</code>, "Create a user withdrawal request"],
                        [<strong>POST</strong>, <code>/user-withdrawal/list</code>, "List withdrawal requests"],
                        [<strong>POST</strong>, <code>/user-withdrawal/approve</code>, "Approve a withdrawal"],
                        [<strong>POST</strong>, <code>/user-withdrawal/decline</code>, "Decline a withdrawal"],
                        [<strong>POST</strong>, <code>/user-withdrawal/status</code>, "Check withdrawal status"],
                        [<strong>POST</strong>, <code>/user-withdrawal/balance</code>, "Check wallet balance"],
                        [<strong>GET</strong>, <code>/token/convert</code>, "Currency conversion (crypto ↔ fiat)"],
                        [<strong>GET</strong>, <code>/token/supported-currencies</code>, "List supported currencies"],
                        [<strong>POST</strong>, <code>/apps/webhook/update</code>, "Configure webhook URL"],
                        [<strong>POST</strong>, <code>/apps/webhook/logs</code>, "Get webhook delivery logs"],
                      ]}
                    />
                  </div>

                  {/* ════════════════ AUTHENTICATION ════════════════ */}
                  <div id="section-auth" className="api-doc-section">
                    <h1>🔐 Authentication</h1>
                    <p>All API calls require three credentials in the request body or as query parameters:</p>

                    <ParamTable rows={[
                      ["appId", "string", "✅", "Your Application ID"],
                      ["apiKey", "string", "✅", "Your API Key"],
                      ["secretKey", "string", "✅", "Your Secret Key"],
                    ]} />

                    <div style={styles.tipBox}>
                      <strong>💡 Tip:</strong> Find your credentials in <strong>Dashboard → Apps → App Details</strong>.
                    </div>

                    <h5 style={styles.subHeading}>Example Headers</h5>
                    <pre style={styles.codeBlock}>{`Content-Type: application/json`}</pre>

                    <h5 style={styles.subHeading}>Credentials in Request Body</h5>
                    <pre style={styles.codeBlock}>{`{
  "appId": "YOUR_APP_ID",
  "apiKey": "YOUR_API_KEY",
  "secretKey": "YOUR_SECRET_KEY",
  ...other parameters
}`}</pre>

                    <h5 style={styles.subHeading} className="mt-4">🚫 IP Whitelisting (403 Forbidden)</h5>
                    <p>If you have <strong>IP Whitelisting</strong> enabled in your <Link to="/settings" style={{color: "#FFCE31", textDecoration: "none"}}>Dashboard Settings</Link>, any request originating from an unregistered IP address will automatically be blocked across all Deposit and Withdrawal endpoints.</p>
                    
                    <pre style={styles.responseBlock}>{`// Status 403 Forbidden Response Example
{
  "response": {
    "message": "Access denied. IP address 29.39.189.120 is not whitelisted.",
    "error": "Forbidden",
    "statusCode": 403
  },
  "status": 403,
  "options": {},
  "message": "Access denied. IP address 29.39.189.120 is not whitelisted.",
  "name": "ForbiddenException"
}`}</pre>
                    <div style={styles.tipBox}>
                      <strong>💡 Note:</strong> You can configure your whitelisted IPs at any time to regain access. Subnet CIDR blocks (e.g., <code>192.168.1.0/24</code>) are natively supported for dynamic cloud architectures.
                    </div>
                  </div>

                  {/* ════════════════ SUPPORTED TOKENS ════════════════ */}
                  <div id="section-tokens" className="api-doc-section">
                    <h1>Supported Tokens</h1>
                    <p>List of all supported tokens with their minimum deposit requirements.</p>
                    <div className="table-responsive">
                      <table className="table api-param-table">
                        <thead>
                          <tr><th>Token Name</th><th>Network</th><th>Code</th><th>Min Deposit</th></tr>
                        </thead>
                        <tbody>
                          {tokens.length > 0 ? (
                            tokens.map((token, index) => (
                              <tr key={index}>
                                <td>{token.symbol}</td>
                                <td>{token.network}</td>
                                <td><code>{token.code}</code></td>
                                <td>{token.minDeposit || 0}</td>
                              </tr>
                            ))
                          ) : (
                            <tr><td colSpan="4" className="text-center">Loading tokens...</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <h4>Supported Fiat Currencies</h4>
                    <SimpleTable
                      headers={["Currency", "Code", "Symbol"]}
                      rows={
                        fiatCurrencies.length > 0
                          ? fiatCurrencies.map(fc => [fc.name, <code>{fc.code}</code>, fc.symbol])
                          : [["Loading...", "", ""]]
                      }
                    />
                  </div>

                  {/* ════════════════ DEPOSIT API ════════════════ */}
                  <div id="section-deposit" className="api-doc-section">
                    <h1>💰 Deposit API</h1>
                    <p>Create payment links to accept crypto or fiat payments from your customers.</p>

                    <div style={styles.baseUrl}>
                      <strong>Base URL:</strong> <code>{process.env.REACT_APP_API_URL}</code>
                    </div>

                    {/* ── Create Crypto Payment ── */}
                    <h4>1️⃣ Create Crypto Payment Link</h4>
                    <p>Generate a payment link for crypto payments.</p>
                    <div style={styles.endpointPost}>{`POST /payment-link/add`}</div>

                    <h5 style={styles.subHeading}>Request Body</h5>
                    <ParamTable rows={[
                      ["appId", "string", "✅", "Your App ID"],
                      ["apiKey", "string", "✅", "Your API Key"],
                      ["secretKey", "string", "✅", "Your Secret Key"],
                      ["code", "string", "✅", "Token code from Supported Tokens (e.g., USDT.TRX)"],
                      ["amount", "string", "✅", "Payment amount"],
                      ["buyerEmail", "string", "✅", "Customer email address"],
                      ["transactionType", "string", "✅", 'Set to "CRYPTO"'],
                      ["metadata", "object", "❌", "Custom JSON data for tracking (see below)"],
                      ["redirectUrl", "string", "❌", "URL to redirect the buyer after successful payment. If not provided, buyer stays on the payment page."],
                    ]} />

                    <h5 style={styles.subHeading}>Example Request</h5>
                    <pre style={styles.codeBlock}>{`{
  "appId": "YOUR_APP_ID",
  "apiKey": "YOUR_API_KEY",
  "secretKey": "YOUR_SECRET_KEY",
  "code": "USDT.TRX",
  "amount": "100",
  "buyerEmail": "customer@example.com",
  "transactionType": "CRYPTO",
  "redirectUrl": "https://yoursite.com/payment/success",
  "metadata": {
    "orderId": "ORD-2024-001",
    "userId": "user_789"
  }
}`}</pre>

                    <hr style={styles.hr} />

                    {/* ── Create Fiat Payment ── */}
                    <h4>2️⃣ Create Fiat Payment Link</h4>
                    <p>Generate a payment link for fiat currency payments. Uses the same endpoint with additional <code>fiatCurrency</code> field.</p>
                    <div style={styles.endpointPost}>{`POST /payment-link/add`}</div>

                    <h5 style={styles.subHeading}>Additional Parameters</h5>
                    <ParamTable rows={[
                      ["transactionType", "string", "✅", 'Set to "FIAT"'],
                      ["fiatCurrency", "string", "✅", `Fiat code: ${fiatCurrencies.length > 0 ? fiatCurrencies.map(fc => fc.code).join(", ") : "loading..."}`],
                    ]} />

                    <h5 style={styles.subHeading}>Example Request</h5>
                    <pre style={styles.codeBlock}>{`{
  "appId": "YOUR_APP_ID",
  "apiKey": "YOUR_API_KEY",
  "secretKey": "YOUR_SECRET_KEY",
  "code": "USDC.BNB",
  "amount": "100",
  "buyerEmail": "customer@example.com",
  "transactionType": "FIAT",
  "fiatCurrency": "try",
  "redirectUrl": "https://yoursite.com/payment/success"
}`}</pre>

                    <div style={styles.infoBox}>
                      <strong>🔀 Redirect URL:</strong> When you provide a <code>redirectUrl</code>, the buyer will be <strong>automatically redirected</strong> to your specified URL 3 seconds after a successful payment. The following parameters are appended as query strings:
                      <br /><br />
                      <strong>Core Parameters (always sent):</strong>
                      <ul style={{margin: '5px 0', paddingLeft: '20px'}}>
                        <li><code>transactionId</code> — PayCoinz payment ID</li>
                        <li><code>status</code> — Payment status (e.g., SUCCESS)</li>
                        <li><code>currency</code> — Token code (e.g., USDT.TRX)</li>
                        <li><code>symbol</code> — Token symbol (e.g., USDT)</li>
                        <li><code>amount</code> — Expected amount</li>
                        <li><code>receivedAmount</code> — Actual received amount</li>
                        <li><code>chainId</code> — Blockchain network ID</li>
                      </ul>
                      <strong>Blockchain Parameters (when available):</strong>
                      <ul style={{margin: '5px 0', paddingLeft: '20px'}}>
                        <li><code>hash</code> — Transaction hash</li>
                        <li><code>fromAddress</code> — Sender wallet address</li>
                      </ul>
                      <strong>Fiat Parameters (for FIAT transactions):</strong>
                      <ul style={{margin: '5px 0', paddingLeft: '20px'}}>
                        <li><code>transactionType</code> — CRYPTO or FIAT</li>
                        <li><code>fiatCurrency</code> — Fiat currency code (e.g., TRY)</li>
                        <li><code>fiatAmount</code> — Original fiat amount</li>
                        <li><code>pricePerCoin</code> — Conversion rate used</li>
                        <li><code>cryptoAmount</code> — Converted crypto amount</li>
                      </ul>
                      <strong>Metadata Parameters (if provided):</strong>
                      <ul style={{margin: '5px 0', paddingLeft: '20px'}}>
                        <li>Each metadata key is prefixed with <code>metadata_</code> (e.g., <code>metadata_orderId=ORD-123</code>)</li>
                      </ul>
                      <br />
                      <strong>Example:</strong><br />
                      <code style={{fontSize: '12px', wordBreak: 'break-all'}}>
                        https://yoursite.com/success?transactionId=PAY123&status=SUCCESS&currency=USDT.TRX&symbol=USDT&amount=100&receivedAmount=100&chainId=TRON&hash=abc123&transactionType=FIAT&fiatCurrency=TRY&fiatAmount=1250&metadata_orderId=ORD-001
                      </code>
                    </div>

                    <hr style={styles.hr} />

                    <h5 style={styles.subHeading}>Error Responses (Validation & Auth)</h5>
                    <p>If the provided data violates validation rules or credentials are invalid, you will receive an HTTP error status code:</p>
                    <SimpleTable
                      headers={["Status Code", "Error", "Common Reason"]}
                      rows={[
                        [<code>400 Bad Request</code>, "Validation Rules", "The provided fiat or crypto amount is below your token's Minimum Deposit limit, or the payload is missing required fields like fiatCurrency."],
                        [<code>401 Unauthorized</code>, "Authentication", "Invalid App ID, API Key, or Secret Key."],
                      ]}
                    />

                    <h5 style={styles.subHeading}>Example 400 Bad Request</h5>
                    <pre style={styles.responseBlock}>{`{
  "message": "For TRON network TRX min deposit value is 10 TRX. Your try 50 converts to 3.541989 TRX which is below the minimum.",
  "error": "Bad Request",
  "statusCode": 400
}`}</pre>

                    <hr style={styles.hr} />

                    {/* ── Check Payment Status ── */}
                    <h4>3️⃣ Check Payment Status</h4>
                    <p>Get the current status of a payment by its ID.</p>
                    <div style={styles.endpointGet}>{`GET /payment-link/get-tx-status?paymentId=PAYMENT_ID`}</div>

                    <h5 style={styles.subHeading}>Payment Statuses</h5>
                    <SimpleTable
                      headers={["Status", "Description"]}
                      rows={[
                        [<code>PENDING FROM BUYER SIDE</code>, "Awaiting user to send crypto"],
                        [<code>PARTIALLY SUCCESS</code>, "Payment received, being processed"],
                        [<code>SUCCESS</code>, "Payment fully confirmed"],
                        [<code>EXPIRED</code>, "Payment link expired (no payment received in time)"],
                      ]}
                    />

                    <hr style={styles.hr} />

                    {/* ── Payment Metadata ── */}
                    <h4>4️⃣ Payment Metadata</h4>
                    <p>Attach custom tracking data to payments using the <code>metadata</code> field. This data is:</p>
                    <ul>
                      <li>✅ Automatically included in all webhook payloads</li>
                      <li>✅ Returned in payment status queries</li>
                      <li>✅ Displayed in merchant and admin dashboards</li>
                      <li>✅ Flexible — accepts any JSON structure</li>
                    </ul>

                    <h5 style={styles.subHeading}>Example Metadata</h5>
                    <pre style={styles.codeBlock}>{`"metadata": {
  "orderId": "ORD-12345",
  "userId": "user_789",
  "productName": "Premium Package",
  "quantity": 2,
  "customerTier": "VIP"
}`}</pre>

                    <h5 style={styles.subHeading}>Metadata in Webhook Payload</h5>
                    <pre style={styles.responseBlock}>{`{
  "event": "payment.confirmed",
  "paymentId": "65abc123...",
  "orderId": "65abc123...",
  "amount": "100",
  "currency": "USDT.TRX",
  "status": "PARTIALLY SUCCESS",
  "transactionType": "CRYPTO",
  "fiatCurrency": null,
  "fiatAmount": null,
  "metadata": {
    "orderId": "ORD-12345",
    "userId": "user_789",
    "productName": "Premium Package"
  },
  "timestamp": 1742200000000,
  "signature": "sha256=...",
  "data": {
    "hash": "0x123abc...",
    "fromAddress": "0xSender...",
    "toAddress": "0xReceiver...",
    "blockNumber": "45678901",
    "chainId": "97",
    "recivedAmount": "100.50"
  }
}`}</pre>

                    <div style={styles.infoBox}>
                      <strong>💡 Pro Tip:</strong> Use metadata to eliminate the need for separate database lookups. Store all relevant order/user information directly with the payment.
                    </div>
                  </div>

                  {/* ════════════════ WITHDRAWAL API ════════════════ */}
                  <div id="section-withdrawal" className="api-doc-section">
                    <h1>💸 Withdrawal API</h1>
                    <p>Enable your platform users to request crypto withdrawals. As a merchant, you can approve, decline, or auto-approve withdrawals based on configured limits.</p>

                    <div style={styles.baseUrl}>
                      <strong>Base URL:</strong> <code>{process.env.REACT_APP_API_URL}</code>
                    </div>

                    {/* ── 1. Create Withdrawal ── */}
                    <h4>1️⃣ Create Withdrawal Request</h4>
                    <p>Submit a withdrawal request on behalf of a user. If the amount is within your auto-approval limits, it will be approved automatically.</p>
                    <div style={styles.endpointPost}>{`POST /user-withdrawal/request`}</div>

                    <h5 style={styles.subHeading}>Request Body</h5>
                    <ParamTable rows={[
                      ["appId", "string", "✅", "Your App ID"],
                      ["apiKey", "string", "✅", "Your API Key"],
                      ["secretKey", "string", "✅", "Your Secret Key"],
                      ["userId", "string", "✅", "Unique user identifier from your platform"],
                      ["userEmail", "string", "❌", "User's email address"],
                      ["userName", "string", "❌", "User's display name"],
                      ["amount", "string", "✅", "Withdrawal amount"],
                      ["code", "string", "✅", "Token code (e.g., USDT.TRX, USDC.BNB)"],
                      ["walletAddress", "string", "✅", "User's wallet address to receive funds"],
                      ["externalReference", "string", "❌", "Your internal reference ID for tracking"],
                      ["transactionType", "string", "❌", 'Set to "FIAT" for fiat withdrawals, defaults to "CRYPTO"'],
                      ["fiatCurrency", "string", "❌", `Fiat code (e.g., ${fiatCurrencies.length > 0 ? fiatCurrencies.slice(0, 3).map(fc => fc.code).join(", ") : "loading..."}) — Required if transactionType is FIAT`],
                    ]} />

                    <h5 style={styles.subHeading}>Example Request (Crypto Withdrawal)</h5>
                    <pre style={styles.codeBlock}>{`{
  "appId": "YOUR_APP_ID",
  "apiKey": "YOUR_API_KEY",
  "secretKey": "YOUR_SECRET_KEY",
  "userId": "user_456",
  "userEmail": "user@example.com",
  "userName": "John Doe",
  "amount": "50",
  "code": "USDT.TRX",
  "walletAddress": "TXxRGTy...userWallet",
  "externalReference": "WD-2024-001"
}`}</pre>

                    <h5 style={styles.subHeading}>Example Request (Fiat Auto-Conversion Withdrawal)</h5>
                    <pre style={styles.codeBlock}>{`{
  "appId": "YOUR_APP_ID",
  "apiKey": "YOUR_API_KEY",
  "secretKey": "YOUR_SECRET_KEY",
  "userId": "user_456",
  "amount": "100",
  "code": "USDC.BNB",
  "walletAddress": "0xYourBscAddress...",
  "transactionType": "FIAT",
  "fiatCurrency": "try"
}`}</pre>

                    <h5 style={styles.subHeading}>Error Responses (Validation & Auth)</h5>
                    <p>If the request payload contains invalid values, or the user exceeds their withdrawal limits, the API returns standard HTTP error codes.</p>
                    <SimpleTable
                      headers={["Status Code", "Error", "Common Reason"]}
                      rows={[
                        [<code>400 Bad Request</code>, "Validation Rules", "The amount is below the merchant's Minimum Withdrawal limit, exceeds Daily Limits, violates the Cooldown Period, or has missing fields."],
                        [<code>401 Unauthorized</code>, "Authentication", "Invalid App ID, API Key, or Secret Key."],
                      ]}
                    />

                    <h5 style={styles.subHeading}>Example 400 Bad Request</h5>
                    <pre style={styles.responseBlock}>{`{
  "message": "Minimum withdrawal amount is 10 USD",
  "error": "Bad Request",
  "statusCode": 400
}`}</pre>

                    <hr style={styles.hr} />

                    {/* ── 2. List Withdrawals ── */}
                    <h4>2️⃣ List Withdrawal Requests</h4>
                    <p>Get a paginated list of withdrawal requests with optional filters.</p>
                    <div style={styles.endpointPost}>{`POST /user-withdrawal/list`}</div>

                    <h5 style={styles.subHeading}>Request Body</h5>
                    <ParamTable rows={[
                      ["appId", "string", "✅", "Your App ID"],
                      ["apiKey", "string", "✅", "Your API Key"],
                      ["secretKey", "string", "✅", "Your Secret Key"],
                      ["status", "string", "❌", "Filter: PENDING, AUTO_APPROVED, APPROVED, PROCESSING, SUCCESS, FAILED, DECLINED"],
                      ["userId", "string", "❌", "Filter by user ID"],
                      ["pageNo", "number", "❌", "Page number (default: 1)"],
                      ["limitVal", "number", "❌", "Items per page (default: 10)"],
                      ["startDate", "string", "❌", "Filter start date"],
                      ["endDate", "string", "❌", "Filter end date"],
                    ]} />

                    <h5 style={styles.subHeading}>cURL Example</h5>
                    <pre style={styles.codeBlock}>{`curl -X POST "${process.env.REACT_APP_API_URL}/user-withdrawal/list" \\
  -H "Content-Type: application/json" \\
  -d '{
    "appId": "YOUR_APP_ID",
    "apiKey": "YOUR_API_KEY",
    "secretKey": "YOUR_SECRET_KEY",
    "status": "PENDING",
    "pageNo": 1,
    "limitVal": 10
  }'`}</pre>

                    <hr style={styles.hr} />

                    {/* ── 3. Approve ── */}
                    <h4>3️⃣ Approve Withdrawal</h4>
                    <p>Approve a pending withdrawal. Funds will be automatically transferred to the user's wallet.</p>
                    <div style={styles.endpointPost}>{`POST /user-withdrawal/approve`}</div>

                    <h5 style={styles.subHeading}>Request Body</h5>
                    <ParamTable rows={[
                      ["appId", "string", "✅", "Your App ID"],
                      ["apiKey", "string", "✅", "Your API Key"],
                      ["secretKey", "string", "✅", "Your Secret Key"],
                      ["withdrawalId", "string", "✅", "ID of the withdrawal to approve"],
                      ["note", "string", "❌", "Optional approval note"],
                    ]} />

                    <h5 style={styles.subHeading}>Example Request</h5>
                    <pre style={styles.codeBlock}>{`{
  "appId": "YOUR_APP_ID",
  "apiKey": "YOUR_API_KEY",
  "secretKey": "YOUR_SECRET_KEY",
  "withdrawalId": "67abc456...",
  "note": "Approved by admin"
}`}</pre>

                    <hr style={styles.hr} />

                    {/* ── 4. Decline ── */}
                    <h4>4️⃣ Decline Withdrawal</h4>
                    <p>Decline a pending withdrawal request with a reason.</p>
                    <div style={styles.endpointPost}>{`POST /user-withdrawal/decline`}</div>

                    <h5 style={styles.subHeading}>Request Body</h5>
                    <ParamTable rows={[
                      ["appId", "string", "✅", "Your App ID"],
                      ["apiKey", "string", "✅", "Your API Key"],
                      ["secretKey", "string", "✅", "Your Secret Key"],
                      ["withdrawalId", "string", "✅", "ID of the withdrawal to decline"],
                      ["reason", "string", "✅", "Reason for declining"],
                    ]} />

                    <h5 style={styles.subHeading}>Example Request</h5>
                    <pre style={styles.codeBlock}>{`{
  "appId": "YOUR_APP_ID",
  "apiKey": "YOUR_API_KEY",
  "secretKey": "YOUR_SECRET_KEY",
  "withdrawalId": "67abc456...",
  "reason": "Suspicious activity detected"
}`}</pre>

                    <hr style={styles.hr} />

                    {/* ── 5. Check Status ── */}
                    <h4>5️⃣ Check Withdrawal Status</h4>
                    <p>Get the current status of a specific withdrawal request.</p>
                    <div style={styles.endpointPost}>{`POST /user-withdrawal/status`}</div>

                    <h5 style={styles.subHeading}>Example Request</h5>
                    <pre style={styles.codeBlock}>{`{
  "appId": "YOUR_APP_ID",
  "apiKey": "YOUR_API_KEY",
  "secretKey": "YOUR_SECRET_KEY",
  "withdrawalId": "67abc456..."
}`}</pre>

                    <h5 style={styles.subHeading}>Withdrawal Statuses</h5>
                    <SimpleTable
                      headers={["Status", "Description"]}
                      rows={[
                        [<code>PENDING</code>, "Awaiting merchant approval"],
                        [<code>AUTO_APPROVED</code>, "Auto-approved (within configured limits)"],
                        [<code>APPROVED</code>, "Manually approved by merchant"],
                        [<code>PROCESSING</code>, "Transfer in progress on blockchain"],
                        [<code>SUCCESS</code>, "Withdrawal completed successfully"],
                        [<code>FAILED</code>, "Transfer failed"],
                        [<code>DECLINED</code>, "Declined by merchant"],
                      ]}
                    />

                    <hr style={styles.hr} />

                    {/* ── 6. Check Balance ── */}
                    <h4>6️⃣ Check App Wallet Balance</h4>
                    <p>Get the available balance for <strong>all supported tokens</strong> in your app wallet. Useful before approving withdrawals or checking funds across all chains.</p>
                    <div style={styles.endpointPost}>{`POST /user-withdrawal/balance`}</div>

                    <h5 style={styles.subHeading}>Request Body</h5>
                    <ParamTable rows={[
                      ["appId", "string", "✅", "Your App ID"],
                      ["apiKey", "string", "✅", "Your API Key"],
                      ["secretKey", "string", "✅", "Your Secret Key"],
                    ]} />

                    <h5 style={styles.subHeading}>Example Request</h5>
                    <pre style={styles.codeBlock}>{`{
  "appId": "YOUR_APP_ID",
  "apiKey": "YOUR_API_KEY",
  "secretKey": "YOUR_SECRET_KEY"
}`}</pre>

                    <h5 style={styles.subHeading}>Example Response</h5>
                    <pre style={styles.responseBlock}>{`{
  "success": true,
  "data": {
    "appId": "699704d74845ee20aae4ef3a",
    "balances": [
      {
        "tokenId": "693c0338fae2bdf92e9378a0",
        "symbol": "BTC",
        "code": "BTC",
        "chainId": "BTC",
        "network": "BITCOIN",
        "walletAddress": "bc1qvy3qywzx2ufyh2...",
        "balance": "0.00125000"
      },
      {
        "tokenId": "693c0338fae2bdf92e9378a1",
        "symbol": "USDT",
        "code": "USDT.TRX",
        "chainId": "TRON",
        "network": "TRON",
        "walletAddress": "TZJ6AhDXLEutssn1dy8o...",
        "balance": "1250.500000"
      },
      {
        "tokenId": "693c0338fae2bdf92e9378a8",
        "symbol": "TRX",
        "code": "TRX",
        "chainId": "TRON",
        "network": "TRON",
        "walletAddress": "TZJ6AhDXLEutssn1dy8o...",
        "balance": "500.000000"
      },
      {
        "tokenId": "693c0338fae2bdf92e9378ab",
        "symbol": "ETH",
        "code": "ETH",
        "chainId": "11155111",
        "network": "ETHEREUM",
        "walletAddress": "0x108D199583a17B05...",
        "balance": "0.050000"
      }
    ]
  }
}`}</pre>

                    <div style={styles.infoBox}>
                      <strong>💡 Note:</strong> The response includes balances for <strong>all supported tokens</strong> across all chains (Bitcoin, TRON, Ethereum, BNB, Polygon). Each entry shows the wallet address and real-time on-chain balance. A balance of "Error" means the balance could not be fetched at that time.
                    </div>

                    <hr style={styles.hr} />

                    {/* ── Withdrawal Flow ── */}
                    <h4>📋 Withdrawal Flow</h4>
                    <ol>
                      <li><strong>User submits request:</strong> Call <code>POST /user-withdrawal/request</code> with user details and amount</li>
                      <li><strong>Auto-approval check:</strong> If amount is within your configured auto-approval limits, it's auto-approved</li>
                      <li><strong>Manual approval:</strong> For larger amounts, review and approve/decline via API or dashboard</li>
                      <li><strong>Transfer:</strong> On approval, funds are automatically transferred to the user's wallet</li>
                      <li><strong>Webhook notification:</strong> A webhook is sent when the withdrawal status changes</li>
                    </ol>

                    <div style={styles.tipBox}>
                      <strong>💡 Tip:</strong> Configure auto-approval limits in <strong>Dashboard → Withdrawal Settings</strong> to automatically process small withdrawals without manual intervention.
                    </div>
                  </div>

                  {/* ════════════════ CURRENCY CONVERSION API ════════════════ */}
                  <div id="section-convert" className="api-doc-section">
                    <h1>💱 Currency Conversion API</h1>
                    <p>Convert between crypto and fiat currencies in real-time using live market rates. This is a public endpoint — no authentication required.</p>

                    <div style={styles.baseUrl}>
                      <strong>Base URL:</strong> <code>{process.env.REACT_APP_API_URL}</code>
                    </div>

                    {/* ── Convert ── */}
                    <h4>1️⃣ Convert Currency</h4>
                    <p>Convert an amount between any supported crypto and fiat currency pair.</p>
                    <div style={styles.endpointGet}>{`GET /token/convert?from=USDT.TRX&to=usd&amount=100`}</div>

                    <h5 style={styles.subHeading}>Query Parameters</h5>
                    <ParamTable rows={[
                      ["from", "string", "✅", "Source currency — crypto code from Supported Tokens (e.g., USDT.TRX) or fiat code (e.g., usd)"],
                      ["to", "string", "✅", "Target currency — crypto code or fiat code"],
                      ["amount", "number", "✅", "Amount to convert (must be positive)"],
                    ]} />

                    <h5 style={styles.subHeading}>Supported Currencies</h5>
                    <SimpleTable
                      headers={["Type", "Supported Values"]}
                      rows={[
                        [<strong>Crypto</strong>, tokens.length > 0 ? tokens.map(t => t.code).join(", ") : "Loading from tokens table..."],
                        [<strong>Fiat</strong>, fiatCurrencies.length > 0 ? fiatCurrencies.map(fc => fc.code.toUpperCase()).join(", ") : "Loading from database..."],
                      ]}
                    />

                    <h5 style={styles.subHeading}>Example Request — Crypto to Fiat</h5>
                    <pre style={styles.codeBlock}>{`curl "${process.env.REACT_APP_API_URL}/token/convert?from=USDT.TRX&to=usd&amount=100"`}</pre>

                    <h5 style={styles.subHeading}>Example Response</h5>
                    <pre style={styles.responseBlock}>{`{
  "success": true,
  "from": "USDT.TRX",
  "to": "USD",
  "amount": 100,
  "convertedAmount": 100.05,
  "rate": 1.0005,
  "timestamp": "2026-03-02T10:00:00.000Z"
}`}</pre>

                    <h5 style={styles.subHeading}>More Examples</h5>
                    <pre style={styles.codeBlock}>{`# Fiat to Crypto
curl "${process.env.REACT_APP_API_URL}/token/convert?from=usd&to=BTC.BTC&amount=1000"

# Crypto to Crypto
curl "${process.env.REACT_APP_API_URL}/token/convert?from=BTC.BTC&to=ETH.ETH&amount=1"

# Fiat to Fiat
curl "${process.env.REACT_APP_API_URL}/token/convert?from=usd&to=try&amount=100"`}</pre>

                    <hr style={styles.hr} />

                    {/* ── Get Supported Currencies ── */}
                    <h4>2️⃣ Get Supported Currencies</h4>
                    <p>Retrieve the list of all supported crypto and fiat currencies for conversion.</p>
                    <div style={styles.endpointGet}>{`GET /token/supported-currencies`}</div>

                    <h5 style={styles.subHeading}>Example Response</h5>
                    <pre style={styles.responseBlock}>{`{
  "crypto": [
    { "code": "USDT.TRX", "symbol": "USDT", "network": "TRON" },
    { "code": "USDC.BNB", "symbol": "USDC", "network": "BNB Smart Chain" },
    { "code": "BTC.BTC", "symbol": "BTC", "network": "Bitcoin" }
  ],
  "fiat": [
    { "code": "TRY", "name": "Turkish Lira" },
    { "code": "USD", "name": "US Dollar" },
    { "code": "EUR", "name": "Euro" },
    { "code": "GBP", "name": "British Pound" }
  ]
}`}</pre>

                    <div style={styles.infoBox}>
                      <strong>💡 Note:</strong> The conversion API uses real-time market rates. Prices may vary slightly between requests.
                    </div>
                  </div>

                  {/* ════════════════ WEBHOOK ════════════════ */}
                  <div id="section-webhook" className="api-doc-section">
                    <h1>📚 Webhook API Guide</h1>
                    <p>Receive real-time payment notifications on your server. Webhooks allow you to automate order fulfillment and keep your systems in sync with payment status changes.</p>

                    <div style={styles.baseUrl}>
                      <strong>Base URL:</strong> <code>{process.env.REACT_APP_API_URL}</code>
                    </div>

                    {/* Configure Webhook */}
                    <h4>1️⃣ Configure Webhook URL</h4>
                    <p>You can configure your webhook URL in two ways:</p>

                    <h5 style={styles.subHeading}>✨ Option 1: Using the Dashboard (Recommended)</h5>
                    <ul>
                      <li>Navigate to <strong>Webhook Settings</strong> (<code>/webhook-settings</code>)</li>
                      <li>Enter your webhook URL (e.g., <code>https://your-server.com/webhook</code>)</li>
                      <li>Click <strong>Save</strong> to activate</li>
                      <li>Copy your <strong>Webhook Secret</strong> for signature verification</li>
                    </ul>

                    <h5 style={styles.subHeading}>🔧 Option 2: Using the API</h5>
                    <div style={styles.endpointPost}>{`POST /apps/webhook/update`}</div>

                    <ParamTable rows={[
                      ["appId", "string", "✅", "Your App ID"],
                      ["apiKey", "string", "✅", "Your API Key"],
                      ["secretKey", "string", "✅", "Your Secret Key"],
                      ["webhookUrl", "string", "✅", "Your webhook endpoint URL"],
                      ["webhookSecret", "string", "❌", "Secret for signature verification"],
                    ]} />

                    <h5 style={styles.subHeading}>Example Request</h5>
                    <pre style={styles.codeBlock}>{`{
  "appId": "YOUR_APP_ID",
  "apiKey": "YOUR_API_KEY",
  "secretKey": "YOUR_SECRET_KEY",
  "webhookUrl": "https://your-site.com/webhook",
  "webhookSecret": "your_strong_secret_key"
}`}</pre>

                    <h5 style={styles.subHeading}>Example Response</h5>
                    <pre style={styles.responseBlock}>{`{
  "status": true,
  "message": "Webhook configuration updated successfully",
  "webhookUrl": "https://your-site.com/webhook"
}`}</pre>

                    <hr style={styles.hr} />

                    {/* Get Logs */}
                    <h4>2️⃣ Get Webhook Logs</h4>
                    <p>Retrieve webhook delivery logs for your app to monitor delivery status.</p>
                    <div style={styles.endpointPost}>{`POST /apps/webhook/logs`}</div>

                    <ParamTable rows={[
                      ["appId", "string", "✅", "Your App ID"],
                      ["apiKey", "string", "✅", "Your API Key"],
                      ["secretKey", "string", "✅", "Your Secret Key"],
                      ["pageNo", "number", "❌", "Page number (default: 1)"],
                      ["limitVal", "number", "❌", "Items per page (default: 20)"],
                      ["status", "string", "❌", "Filter: PENDING, SUCCESS, FAILED"],
                      ["event", "string", "❌", "Filter by event type"],
                    ]} />

                    <hr style={styles.hr} />

                    {/* Webhook Events Model 1 - Payments */}
                    <h4>📩 1. Deposit / Payment Webhooks</h4>
                    <p>These events relate to incoming payments and invoice fulfillment. They utilize a nested <code>data</code> object to store blockchain specifics.</p>
                    <SimpleTable
                      headers={["Event", "Description", "Status Payload Field", "Trigger"]}
                      rows={[
                        [<code>payment.initiated</code>, "Payment link created", <code>PENDING FROM BUYER SIDE</code>, "Payment link or invoice is generated"],
                        [<code>payment.confirmed</code>, "Crypto received in mempool", <code>PARTIALLY SUCCESS</code>, "Customer sends partial/full funds on-chain"],
                        [<code>payment.success</code>, "Funds successfully confirmed", <code>SUCCESS</code>, "Transaction meets block confirmation requirement"],
                        [<code>payment.expired</code>, "Payment link expired", <code>EXPIRED</code>, "Payment link reaches expiry time limit before payment"],
                      ]}
                    />

                    <h5 style={styles.subHeading}>Example `payment.success` Payload — Crypto Payment</h5>
                    <pre style={styles.codeBlock}>{`{
  "event": "payment.success",
  "paymentId": "65abc123def456",
  "orderId": "65abc123def456",
  "amount": "100",
  "currency": "USDT.TRX",
  "status": "SUCCESS",
  "transactionType": "CRYPTO",
  "fiatCurrency": null,
  "fiatAmount": null,
  "metadata": { "orderId": "ORD-123" },
  "timestamp": 1707055200000,
  "signature": "sha256=a1b2c3d4...",
  "data": {
    "hash": "0x123abc456def...",
    "fromAddress": "0xSenderAddress...",
    "toAddress": "0xReceiverAddress...",
    "blockNumber": "45678901",
    "chainId": "56",
    "recivedAmount": "100.50"
  }
}`}</pre>

                    <h5 style={styles.subHeading}>Example `payment.success` Payload — FIAT Payment</h5>
                    <pre style={styles.codeBlock}>{`{
  "event": "payment.success",
  "paymentId": "65abc123def456",
  "orderId": "65abc123def456",
  "amount": "0.002000",
  "currency": "TRX",
  "status": "SUCCESS",
  "transactionType": "FIAT",
  "fiatCurrency": "try",
  "fiatAmount": "100",
  "metadata": { "orderId": "ORD-456" },
  "timestamp": 1707055200000,
  "signature": "sha256=a1b2c3d4...",
  "data": {
    "hash": "0x123abc456def...",
    "fromAddress": "0xSenderAddress...",
    "toAddress": "0xReceiverAddress...",
    "blockNumber": "45678901",
    "chainId": "56",
    "recivedAmount": "0.001987"
  }
}`}</pre>

                    <div style={styles.infoBox}>
                      <strong>💡 Important:</strong> For <strong>CRYPTO</strong> payments, <code>fiatCurrency</code> and <code>fiatAmount</code> are always <code>null</code> (never omitted). For <strong>FIAT</strong> payments, both fields contain values. All numeric fields are returned as <strong>strings</strong>. The <code>metadata</code> field is only present if you passed metadata when creating the payment — it is omitted entirely if empty. The <code>signature</code> is computed on the payload <em>before</em> <code>signature</code> is added — strip it before verifying.
                    </div>

                    <hr style={styles.hr} />

                    {/* Webhook Events Model 2 - Withdrawals */}
                    <h4>📤 2. Withdrawal Webhooks</h4>
                    <p>These events relate to outgoing withdrawals. All withdrawal webhooks use a structured payload with a nested <code>user</code> and <code>withdrawal</code> object. FIAT withdrawal fields are included in the payload for cross-referencing the original fiat amount with the crypto sent on-chain.</p>
                    <SimpleTable
                      headers={["Event", "Description", "Status", "Trigger"]}
                      rows={[
                        [<code>withdrawal.pending</code>, "Queued", <code>PENDING</code>, "Awaiting manual merchant approval"],
                        [<code>withdrawal.auto_approved</code>, "Auto-approved", <code>AUTO_APPROVED</code>, "Amount within auto-approval limits — no manual review needed"],
                        [<code>withdrawal.approved</code>, "Approved", <code>APPROVED</code>, "Manually approved by merchant"],
                        [<code>withdrawal.processing</code>, "Processing", <code>PROCESSING</code>, "Currently broadcasting to the blockchain"],
                        [<code>withdrawal.success</code>, "Confirmed", <code>SUCCESS</code>, "Network confirmed withdrawal"],
                        [<code>withdrawal.failed</code>, "Failed", <code>FAILED</code>, "Insufficient balance or dropped transaction"],
                        [<code>withdrawal.declined</code>, "Declined", <code>DECLINED</code>, "Merchant manually rejected withdrawal"]
                      ]}
                    />

                    <h5 style={styles.subHeading}>Example `withdrawal.success` Payload — Crypto Withdrawal</h5>
                    <pre style={styles.codeBlock}>{`{
  "event": "withdrawal.success",
  "withdrawalId": "69b2b6dd25d7885ca93e367",
  "user": {
    "userId": "client_internal_user_123",
    "userEmail": "user@example.com",
    "userName": "John Doe"
  },
  "withdrawal": {
    "amount": "9.876268",
    "tokenSymbol": "TRX",
    "chainId": "TRON",
    "walletAddress": "TEXxRGTyYjUjsq...",
    "txHash": "a1b2c3d4e5f6g7h8i9j0...",
    "adminFee": "0.10000",
    "externalReference": "REF-001",
    "failureReason": null,
    "declineReason": null,
    "transactionType": null,
    "fiatCurrency": null,
    "fiatAmount": null,
    "cryptoAmount": null,
    "pricePerCoin": null
  },
  "status": "SUCCESS",
  "timestamp": 1742200000000,
  "signature": "sha256=aO64JQWqq..."
}`}</pre>

                    <h5 style={styles.subHeading}>Example `withdrawal.success` Payload — FIAT Withdrawal</h5>
                    <pre style={styles.codeBlock}>{`{
  "event": "withdrawal.success",
  "withdrawalId": "69b2b6dd25d7885ca93e368",
  "user": {
    "userId": "client_internal_user_456",
    "userEmail": "user@example.com",
    "userName": "Jane Doe"
  },
  "withdrawal": {
    "amount": "0.002000",
    "tokenSymbol": "TRX",
    "chainId": "TRON",
    "walletAddress": "TXxRGTy...userWallet",
    "txHash": "b2c3d4e5f6g7h8i9j0k1...",
    "adminFee": "0.00005",
    "externalReference": "WD-2024-001",
    "failureReason": null,
    "declineReason": null,
    "transactionType": "FIAT",
    "fiatCurrency": "try",
    "fiatAmount": "100",
    "cryptoAmount": "0.002000",
    "pricePerCoin": "50000.5"
  },
  "status": "SUCCESS",
  "timestamp": 1742200000000,
  "signature": "sha256=bP75KRXrr..."
}`}</pre>

                    <div style={styles.infoBox}>
                      <strong>💡 Important:</strong> For <strong>CRYPTO</strong> withdrawals, <code>transactionType</code>, <code>fiatCurrency</code>, <code>fiatAmount</code>, <code>cryptoAmount</code>, and <code>pricePerCoin</code> are always <code>null</code>. For <strong>FIAT</strong> withdrawals, <code>amount</code> is the crypto amount sent on-chain, while <code>fiatAmount</code> is the original fiat value the user requested. All numeric fields are strings.
                    </div>

                    <hr style={styles.hr} />

                    {/* Headers */}
                    <h4>📨 Webhook Request Headers</h4>
                    <SimpleTable
                      headers={["Header", "Description"]}
                      rows={[
                        [<code>Content-Type</code>, "application/json"],
                        [<code>X-Webhook-Event</code>, "Event type (e.g. payment.confirmed)"],
                        [<code>X-Webhook-Env</code>, "Environment identifier (returns 'mainnet' or 'testnet')"],
                        [<code>X-Webhook-Signature</code>, "HMAC SHA256 signature calculated using your Webhook Secret/Secret Key"],
                      ]}
                    />

                    <div style={styles.infoBox}>
                      <strong>💡 Env Separation Tip:</strong> You can safely route the incoming webhook event on your server by reading the <code>X-Webhook-Env</code> header. This makes it incredibly easy to use the same logic file to process Testnet payloads into a sandbox database without risking production funds.
                    </div>

                    <hr style={styles.hr} />

                    {/* Retries */}
                    <h4>🔁 Automatic Retries</h4>
                    <div style={styles.infoBox}>
                      <strong>💡 Note:</strong> If your server does not return a successful response code (200-299) or times out, Paycoinz will automatically retry sending the exact same webhook payload <strong>up to 5 times</strong> at increasing intervals (1 min, 5 min, 15 min, 1 hour).
                    </div>

                    <hr style={styles.hr} />

                    {/* Signature Verification */}
                    <h4>🔏 Signature Verification</h4>
                    <p>Always verify webhook authenticity using the signature provided in the <code>X-Webhook-Signature</code> header:</p>

                    <h5 style={styles.subHeading}>Node.js Example</h5>
                    <pre style={styles.codeBlock}>{`const crypto = require('crypto');

function verifyWebhookSignature(payload, receivedSignature, webhookSecret) {
  // IMPORTANT: Remove the 'signature' field before verifying,
  // because the backend signs the payload *before* adding the signature to the body.
  const { signature, ...payloadWithoutSignature } = payload;

  const expectedSignature = \`sha256=\${crypto
    .createHmac('sha256', webhookSecret)
    .update(JSON.stringify(payloadWithoutSignature))
    .digest('hex')}\`;
  
  return crypto.timingSafeEqual(
    Buffer.from(receivedSignature),
    Buffer.from(expectedSignature)
  );
}

// Usage in your Express webhook handler
app.post('/webhook', express.json(), (req, res) => {
  const payload = req.body;
  const signature = req.headers['x-webhook-signature'];
  
  if (!signature || !verifyWebhookSignature(payload, signature, 'YOUR_WEBHOOK_SECRET')) {
    return res.status(401).json({ error: 'Invalid signature' });
  }
  
  console.log('Webhook verified:', payload.event);
  res.status(200).json({ received: true });
});`}</pre>

                    <h5 style={styles.subHeading}>Python Example</h5>
                    <pre style={styles.codeBlock}>{`import hmac
import hashlib
import json
from flask import request

def verify_webhook(payload, received_signature, secret):
    # IMPORTANT: Remove 'signature' field before verifying.
    # The backend signs the payload before adding the signature to the body.
    payload_to_verify = {k: v for k, v in payload.items() if k != 'signature'}

    payload_str = json.dumps(payload_to_verify, separators=(',', ':'))
    
    expected = 'sha256=' + hmac.new(
        secret.encode('utf-8'),
        msg=payload_str.encode('utf-8'),
        digestmod=hashlib.sha256
    ).hexdigest()
    
    return hmac.compare_digest(expected, received_signature)

# Usage in a Flask route
@app.route('/webhook', methods=['POST'])
def webhook_handler():
    payload = request.json
    signature = request.headers.get('X-Webhook-Signature')
    
    if not signature or not verify_webhook(payload, signature, 'YOUR_WEBHOOK_SECRET'):
        return {"error": "Invalid signature"}, 401
        
    print(f"Webhook verified: {payload.get('event')}")
    return {"received": True}, 200`}</pre>

                    <hr style={styles.hr} />

                    {/* Retry Policy */}
                    <h4>🔄 Retry Policy</h4>
                    <p>If your server fails to respond with 2xx status, we'll retry:</p>
                    <SimpleTable
                      headers={["Attempt", "Delay"]}
                      rows={[["1st", "Immediate"], ["2nd", "1 minute"], ["3rd", "5 minutes"], ["4th", "15 minutes"], ["5th", "1 hour"]]}
                    />
                    <p style={{ color: '#666' }}>After 5 failed attempts, the webhook is marked as FAILED.</p>

                    <hr style={styles.hr} />

                    {/* Response Requirements */}
                    <h4>✅ Response Requirements</h4>
                    <p>Your endpoint <strong>must</strong>:</p>
                    <ul>
                      <li>Return HTTP status <code>200-299</code> within <strong>10 seconds</strong></li>
                      <li>Failure to do so triggers the retry mechanism</li>
                    </ul>
                    <p><strong>Recommended Response:</strong></p>
                    <pre style={styles.responseBlock}>{`{ "received": true }`}</pre>

                    <hr style={styles.hr} />

                    {/* Best Practices */}
                    <h4>💡 Best Practices</h4>
                    <SimpleTable
                      headers={["#", "Practice", "Description"]}
                      rows={[
                        ["1", <strong>Always verify signatures</strong>, "Prevent spoofed webhooks from malicious actors"],
                        ["2", <strong>Respond quickly</strong>, "Process async if needed, but respond with 200 immediately"],
                        ["3", <strong>Handle duplicates</strong>, <span>Use <code>paymentId</code> to ensure idempotency</span>],
                        ["4", <strong>Log everything</strong>, "Store payloads for debugging and auditing"],
                        ["5", <strong>Use HTTPS</strong>, "Required for production webhooks (no HTTP)"],
                      ]}
                    />
                  </div>

                  {/* ════════════════ PLAYGROUND ════════════════ */}
                  <div id="section-playground" className="api-doc-section">
                    <h1>Playground</h1>
                    <p>Test the API endpoints below using the interactive Swagger UI.</p>
                    <div id="swagger-ui"></div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Apis;
